import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, Moon, Sun, Package, Calculator } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  const navItems = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.about'), href: '/sobre' },
    { label: t('nav.services'), href: '/servicos' },
    { label: t('nav.quote'), href: '/cotacao' },
    { label: t('nav.contact'), href: '/contato' },
    { label: t('nav.blog'), href: '/blog' },
  ];

  const languages = [
    { code: 'pt', flag: '🇧🇷', name: 'Português' },
    { code: 'es', flag: '🇪🇸', name: 'Español' },
    { code: 'en', flag: '🇺🇸', name: 'English' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-[#2d2d2d]/95 dark:bg-[#1a1a1a]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group cursor-pointer">
              <img 
                src="/assets/logo-wellcargo-white.png" 
                alt="Well Cargo" 
                className="h-14 md:h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className={`relative text-sm font-medium transition-colors duration-200 group ${
                    location.pathname === item.href
                      ? 'text-[#3ECDA3]'
                      : 'text-white hover:text-[#3ECDA3]'
                  }`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#3ECDA3] transition-all duration-300 ${
                    location.pathname === item.href ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </nav>

            {/* Right Side Controls */}
            <div className="flex items-center gap-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="hidden md:flex p-2 text-white hover:text-[#3ECDA3] transition-colors duration-200"
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* Language Selector */}
              <div className="hidden md:flex items-center gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`text-lg transition-all duration-200 hover:scale-110 ${
                      language === lang.code ? 'scale-125 opacity-100' : 'opacity-60 hover:opacity-100'
                    }`}
                    title={lang.name}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-white p-2 hover:text-[#3ECDA3] transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden bg-[#2d2d2d]/98 dark:bg-[#1a1a1a]/98 backdrop-blur-sm overflow-hidden transition-all duration-300 ${
        mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <nav className="px-4 py-4 space-y-4">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className={`block text-base font-medium transition-colors duration-200 ${
                location.pathname === item.href
                  ? 'text-[#3ECDA3]'
                  : 'text-white hover:text-[#3ECDA3]'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          
          {/* Mobile Language & Theme */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <div className="flex items-center gap-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`text-xl transition-all duration-200 ${
                    language === lang.code ? 'scale-125 opacity-100' : 'opacity-60'
                  }`}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 text-white hover:text-[#3ECDA3] transition-colors"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
